// ./src/components/features/Refuels/FuelGradeSelector/FuelGradeSelector.tsx
import React, { useMemo, useEffect } from "react";
import { Select, Group, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";

import {
  getFuelGradeIcon,
  getFuelGradesByType,
  FUEL_GRADE_MAP,
} from "@/config/fuelGrades";

import { DRAWER_COMBOBOX_PROPS } from "@/utils/mantineUiHelpers";

import { AppIcon } from "@/components/generic/AppIcon";

// ============================================
// Types
// ============================================

export interface FuelGradeSelectorProps {
  /** Field name for form binding */
  name?: string;

  /** Currently selected fuel grade */
  value?: string | null | undefined;

  /** Callback fired when fuel grade changes: (name, value) */
  onChange?: (name: string, value: string | null) => void;

  /** Fuel type to filter available grades (e.g., "gasoline", "diesel") */
  fuelType?: string | null | undefined;

  /** Placeholder text */
  placeholder?: string;

  /** Whether the field is required (auto-selects first option if null) */
  required?: boolean;

  /** Error message to display */
  error?: string | null | undefined;

  /** Whether the selector is disabled */
  disabled?: boolean;

  /** Size of the selector */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Label for the field */
  label?: string;
}

// ============================================
// Component
// ============================================

const FuelGradeSelectorComponent: React.FC<FuelGradeSelectorProps> = ({
  name = "fuelGrade",
  value,
  onChange,
  fuelType,
  placeholder,
  required = false,
  error,
  disabled = false,
  size = "md",
  label,
}) => {
  const { t } = useTranslation(["refuels", "common"]);

  // Memoize on fuelType value only, not on t reference
  const grades = useMemo(() => getFuelGradesByType(fuelType), [fuelType]);

  const options = useMemo(
    () =>
      grades.map((grade) => ({
        value: grade.value,
        label: t(`common:${grade.labelKey}`),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [grades]
  );

  useEffect(() => {
    if (options.length === 0) return;

    const valueInOptions = value ? options.some((opt) => opt.value === value) : false;

    if (valueInOptions) return; // Current value is valid, nothing to do

    if (required) {
      // Auto-select first option
      onChange?.(name, options[0].value);
    } else if (value) {
      // Value exists but isn't valid anymore — clear it
      onChange?.(name, null);
    }
  }, [value, options, name, onChange, required]);

  // Get icon for current value
  const currentIcon = useMemo(() => getFuelGradeIcon(value), [value]);

  // Handle selection change
  const handleChange = (selectedValue: string | null) => {
    onChange?.(name, selectedValue);
  };

  // Render option with icon
  const renderOption: React.ComponentProps<typeof Select>["renderOption"] = ({
    option,
  }) => {
    const fuelGrade = FUEL_GRADE_MAP[option.value];
    const iconName = fuelGrade?.iconName ?? "GasStation";

    return (
      <Group gap="sm" wrap="nowrap">
        <AppIcon iconName={iconName} size={20} />
        <Text size="md">{option.label}</Text>
      </Group>
    );
  };

  // Default translations
  const defaultPlaceholder =
    placeholder || t("refuels:form.fields.fuelGrade.placeholder");
  const defaultLabel = label ?? t("refuels:form.fields.fuelGrade.label");

  return (
    <Select
      label={defaultLabel}
      placeholder={defaultPlaceholder}
      data={options}
      value={value || null}
      onChange={handleChange}
      disabled={disabled}
      required={required}
      clearable={!required}
      searchable={false}
      error={error}
      size={size}
      leftSection={<AppIcon iconName={currentIcon} size={18} />}
      renderOption={renderOption}
      comboboxProps={DRAWER_COMBOBOX_PROPS}
    />
  );
};

export const FuelGradeSelector = React.memo(FuelGradeSelectorComponent);